import { useState } from 'react';
import { useScopedEnrollments } from '@/api/enrollments';
import { useEnrollmentCourses } from '@/api/instructor-courses';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Combobox } from '@/components/shared/combobox';
import { cn } from '@/lib/utils';
import {
  Plus,
  GraduationCap,
  User,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { CreateEnrollmentDialog } from './_components/create-enrollment-dialog';
import { coursesForIndividualEnrollment } from '@/lib/enrollment-courses';
import { useProfile } from '@/api/profile';

type StatusKey = 'all' | 'active' | 'completed' | 'expired' | 'cancelled';

const STATUS_MAP: Record<StatusKey, { label: string; status?: string }> = {
  all: { label: 'Todas' },
  active: { label: 'Activas', status: 'active' },
  completed: { label: 'Completadas', status: 'completed' },
  expired: { label: 'Vencidas', status: 'expired' },
  cancelled: { label: 'Canceladas', status: 'cancelled' },
};

const STATUS_BADGES: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  active: { label: 'Activa', color: 'bg-blue-100 text-blue-700', icon: Clock },
  completed: { label: 'Completada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  expired: { label: 'Vencida', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700', icon: XCircle },
};

export function EnrollmentsPage() {
  const [tab, setTab] = useState<StatusKey>('all');
  const [filterCourseId, setFilterCourseId] = useState<number>(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data: profile } = useProfile();
  const { data: coursesRes, isLoading: loadingCourses } = useEnrollmentCourses();
  // Vista previa: publicados (todos) + borradores propios del instructor (profile.id).
  const courses = coursesForIndividualEnrollment(coursesRes?.data ?? [], profile?.id);

  const {
    data: enrollmentsRes,
    isLoading: loadingEnrollments,
    error: enrollmentsError,
  } = useScopedEnrollments({
    per_page: 100,
    status: STATUS_MAP[tab].status,
    course_id: filterCourseId || undefined,
  });

  const enrollments = enrollmentsRes?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Inscripciones</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gestiona las inscripciones de los empleados en tus cursos.
          </p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Inscribir
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        {/* Status tabs */}
        <div className="flex gap-1 overflow-x-auto rounded-lg bg-muted p-1 flex-1">
          {(Object.keys(STATUS_MAP) as StatusKey[]).map((key) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                'flex-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
                tab === key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {STATUS_MAP[key].label}
            </button>
          ))}
        </div>

        {/* Course filter */}
        <div className="w-full sm:w-64">
          <Combobox
            value={filterCourseId ? String(filterCourseId) : ''}
            onChange={(v) => setFilterCourseId(v ? Number(v) : 0)}
            options={courses.map((c) => ({ value: String(c.id), label: c.title }))}
            placeholder="Todos los cursos"
            disabled={loadingCourses}
            clearable
          />
        </div>
      </div>

      {/* Content */}
      {loadingEnrollments ? (
        <LoadingSpinner />
      ) : enrollmentsError ? (
        <ErrorMessage message="No se pudieron cargar las inscripciones." />
      ) : enrollments.length === 0 ? (
        <EmptyState
          title="Sin inscripciones"
          description={`No hay inscripciones ${tab !== 'all' ? STATUS_MAP[tab].label.toLowerCase() : ''} para mostrar.`}
        />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {enrollments.length} inscripci{enrollments.length !== 1 ? 'ones' : 'on'}
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {enrollments.map((e) => {
              const defaultBadge = {
                label: 'Activa',
                color: 'bg-blue-100 text-blue-700',
                icon: Clock,
              };
              const badge = STATUS_BADGES[e.status] ?? defaultBadge;
              const BadgeIcon = badge.icon;
              // The API returns `user` in enrollmentDetailSelect but the Enrollment type
              // doesn't include it. We access it from the raw response.
              const raw = e as typeof e & {
                user?: { id: number; first_name: string; last_name: string; email: string };
              };

              return (
                <div key={e.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                  {/* Course */}
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <GraduationCap className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-card-foreground">
                        {e.course?.title ?? `Curso #${e.course_id}`}
                      </p>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium mt-1',
                          badge.color,
                        )}
                      >
                        <BadgeIcon className="h-3 w-3" />
                        {badge.label}
                      </span>
                    </div>
                  </div>

                  {/* User */}
                  {raw.user && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <User className="h-3.5 w-3.5" />
                      <span>
                        {raw.user.first_name} {raw.user.last_name}
                      </span>
                    </div>
                  )}

                  {/* Progress */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Progreso</span>
                      <span>{e.progress_percent}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          e.progress_percent >= 100 ? 'bg-green-500' : 'bg-primary',
                        )}
                        style={{ width: `${Math.min(e.progress_percent, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>Inscrito el {new Date(e.enrolled_at).toLocaleDateString('es')}</span>
                  </div>
                  {e.due_date && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span>Vence: {new Date(e.due_date).toLocaleDateString('es')}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Dialog */}
      {showCreateDialog && (
        <CreateEnrollmentDialog courses={courses} onClose={() => setShowCreateDialog(false)} />
      )}
    </div>
  );
}
